import React from 'react'
import { Link, NavLink, Redirect } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import NavItem from '../../common/NavItem';
import StatisticIcon from '../../img/statistic.svg';
import StatisticIconActive from '../../img/statistic_fill.svg';
import SaEnkeltTipsIcon from '../../img/sa-enkelt-bulb-line.svg';
import SaEnkeltTipsIconActive from '../../img/sa-enkelt-bulb.svg'
import SaEnkeltHomeIconActive from '../../img/sa-enkelt-active-home.svg'
import SaEnkeltHomeIcon from '../../img/sa-enkelt-home.svg'
import { Possession } from '../../types/types';
import { getDashboardAssociationBookletsQuery } from '../../queries/booklet';
import { getAssociationPublicQuery } from '../../queries/association';

const { REACT_APP_BRAND } = process.env;

interface MainNavigationProps {
  possession?: Possession;
  redirect: boolean;
}

const MainNavigation = (props: MainNavigationProps) => {
  const { possession, redirect } = props;
  const [activeLink, setActiveLink] = React.useState('')
  const associationId = possession?.type === 'association' ? possession._id : possession?.associationId;

  const { data: bookletsData } = useQuery(getDashboardAssociationBookletsQuery, { variables: { associationId: possession?._id }, skip: possession?.type !== 'association' });
  const { data: associationData } = useQuery(getAssociationPublicQuery, { variables: { associationId }, skip: !associationId || possession?.type === 'superAdmin' || possession?.type === 'buyer' });

  const defaultBooklet = bookletsData?.booklets
    ?.filter(booklet => booklet.validFrom)
    ?.sort((a, b) => new Date(b.validFrom).getTime() - new Date(a.validFrom).getTime())[0];

  const isMainAssociation = defaultBooklet?.mainAssociationId === possession?._id;

  const subshopVisibility = associationData?.association?.subshopVisibilityInDashboard;
  const showTeamsMenuOnAssociation = subshopVisibility === 'TEAMS' || subshopVisibility === 'TEAMS_AND_MEMBERS';
  const showSalesMenuOnTeam = subshopVisibility === 'TEAM_MEMBERS' || subshopVisibility === 'TEAMS_AND_MEMBERS';

  let menuItems: React.ReactNode[] = [];
  let redirectPath = '';

  if (!possession) {
    return null;
  }

  const handleActiveLink = (match: any, link: string) => {
    if (match) setActiveLink(link);
    return !!match;
  }

  switch (possession.type) {

    case 'superAdmin':
      redirectPath = `/cities`;
      menuItems = [
        <li key="cities"><Link to="/cities">Zoner</Link></li>,
        <li key="users"><Link to="/users">Användare</Link></li>
      ];
      break;
    case 'zone':
      redirectPath = `/cities/${possession._id}/companies`;
      menuItems = [
        <li key="cities"><Link to="/cities">Zoner</Link></li>,
      ];
      break;
    case 'company':
      redirectPath = `/cities/${possession.zoneId}/companies/${possession._id}/agreements`;
      menuItems = [
        <li key="company-agreements"><Link to={`/cities/companies/${possession._id}/agreements`}>Startsida</Link></li>,
      ];
      break;
    case 'association':
      redirectPath = `/cities/associations/${possession._id}/home`;
      menuItems = [
        <li key='association-home' className='li-no-icon'>
          <NavLink to={`/cities/associations/${possession._id}/home`} isActive={(match: any) => handleActiveLink(match, 'home')}>
            <NavItem title="Start" active={activeLink === 'home'} />
          </NavLink>
        </li>,
        ...(isMainAssociation ? [
          <li key='association-resellers' className='li-no-icon'>
            <NavLink to={`/cities/associations/${possession._id}/resellers`} isActive={(match: any) => handleActiveLink(match, 'resellers')}>
              <NavItem title="Återförs." active={activeLink === 'resellers'} />
            </NavLink>
          </li>
        ] : (showTeamsMenuOnAssociation ? [
          <li key='association-teams' className='li-no-icon'>
            <NavLink to={`/cities/associations/${possession._id}/teams`} isActive={(match: any) => handleActiveLink(match, 'teams')}>
              <NavItem title="Lag" active={activeLink === 'teams'} />
            </NavLink>
          </li>
        ] : [])),
        <li key='association-resources' className='li-no-icon'>
          <NavLink to={`/cities/associations/${possession._id}/resources`} isActive={(match: any) => handleActiveLink(match, 'resources')}>
            <NavItem title="Resurser" active={activeLink === 'resources'} />
          </NavLink>
        </li>,
      ];
      break;
    case 'team':
      redirectPath = `/cities/associations/${possession.associationId}/teams/${possession._id}/home`;
      menuItems = [
        <li key="team-start" className='li-no-icon'>
          <NavLink to={`/cities/associations/${possession.associationId}/teams/${possession._id}/home`} isActive={(match: any) => handleActiveLink(match, 'start')}>
            <NavItem title="Start" active={activeLink === 'start'} />
          </NavLink>
        </li>,
        ...(showSalesMenuOnTeam ? [
          <li key="team-sales" className='li-no-icon'>
            <NavLink to={`/cities/associations/${possession.associationId}/teams/${possession._id}/sales`} isActive={(match: any) => handleActiveLink(match, 'sales')}>
              <NavItem title="Säljare" active={activeLink === 'sales'} />
            </NavLink>
          </li>
        ] : []),
        <li key="team-material" className='li-no-icon'>
          <NavLink to={`/cities/associations/${possession.associationId}/teams/${possession._id}/help`} isActive={(match: any) => handleActiveLink(match, 'material')}>
            <NavItem title="Material" active={activeLink === 'material'} />
          </NavLink>
        </li>
      ]
      break;
    case 'teamMember':
      redirectPath = `/cities/associations/${possession.associationId}/teams/${possession.teamId}/teamMembers/${possession._id}/home`;
      menuItems = [
        <li key="teamMember-home">
          <NavLink to={`/cities/associations/${possession.associationId}/teams/${possession.teamId}/teamMembers/${possession._id}/home`} isActive={(match: any) => handleActiveLink(match, 'home')}>
            <NavItem title="Hem" icon={activeLink === 'home' ? SaEnkeltHomeIconActive : SaEnkeltHomeIcon} active={activeLink === 'home'} />
          </NavLink>
        </li>,
        <li key="teamMember-statistic">
          <NavLink to={`/cities/associations/${possession.associationId}/teams/${possession.teamId}/teamMembers/${possession._id}/statistic`} isActive={(match: any) => handleActiveLink(match, 'teams')}>
            <NavItem title="Statistik" icon={activeLink === 'teams' ? StatisticIconActive : StatisticIcon} active={activeLink === 'teams'} />
          </NavLink>
        </li>,
        <li key="teamMember-help">
          <NavLink to={`/cities/associations/${possession.associationId}/teams/${possession.teamId}/teamMembers/${possession._id}/help`} isActive={(match: any) => handleActiveLink(match, 'help')}>
            <NavItem title="Tips" icon={activeLink === 'help' ? SaEnkeltTipsIconActive : SaEnkeltTipsIcon} active={activeLink === 'help'} />
          </NavLink>
        </li>
      ]
      break;
    case 'buyer':
      redirectPath = '/profile';
      break;
    default:
      break;
  }

  return (
    <header className={`main-header`}>
      <h1 className="mh-logo">{REACT_APP_BRAND === 'CITY' ? 'Cityhäftet' : 'Så Enkelt'}</h1>
      <ul className={`nav-possession ${possession.type === 'superAdmin' ? 'superadmin' : ''}`}>
        {menuItems && menuItems.map((obj, index) => (
          <React.Fragment key={index}>
            {obj}
          </React.Fragment>
        ))}
        {redirect && <Redirect to={redirectPath} push />}
      </ul>
    </header>

  );
}

export default MainNavigation
